import { useEffect, useMemo, useState } from "react";
import { getSharedConversation } from "component/chatbot/share/shareApi";
import { mapSharedChatError } from "component/chatbot/share/shareUtils";
import { PAGE_STATUS } from "./constants";

/**
 * Loads a public shared conversation by URL token.
 * Cancel-safe: ignores results after unmount / token change.
 */
export function useSharedConversation(token) {
  const [status, setStatus] = useState(PAGE_STATUS.LOADING);
  const [conversation, setConversation] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setStatus(PAGE_STATUS.LOADING);
      setConversation(null);

      if (!token || !String(token).trim()) {
        if (!cancelled) setStatus(PAGE_STATUS.UNAVAILABLE);
        return;
      }

      try {
        const data = await getSharedConversation(token);
        if (cancelled) return;

        if (!data?.conversation) {
          setStatus(PAGE_STATUS.UNAVAILABLE);
          return;
        }

        setConversation(data.conversation);
        setStatus(PAGE_STATUS.SUCCESS);
      } catch (err) {
        if (cancelled) return;
        const mapped = mapSharedChatError(err);
        if (mapped === "expired") setStatus(PAGE_STATUS.EXPIRED);
        else if (mapped === "unavailable") setStatus(PAGE_STATUS.UNAVAILABLE);
        else setStatus(PAGE_STATUS.ERROR);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token, retryKey]);

  const messagePairs = useMemo(() => {
    const list = conversation?.messages;
    return Array.isArray(list) ? list : [];
  }, [conversation]);

  const retry = () => setRetryKey((key) => key + 1);

  return {
    status,
    conversation,
    messagePairs,
    retry,
  };
}
